import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Loader } from "@arcadeai/design-system/components/ui/atoms/loader";
import { Wrench } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ToolkitPicker } from "@/features/toolkit-picker";
import { ToolkitPickerTrigger } from "@/features/toolkit-picker/components/toolkit-picker-trigger";
import { useChatToolkits } from "../context";

const LOADING_PHRASES = [
  "Convincing MCP servers to wake up...",
  "Our servers are doing stretches...",
  "Negotiating with the cloud...",
  "MCP servers are grabbing coffee...",
  "Promised the tools we'd be quick...",
  "Our hamsters are spinning faster...",
  "Giving the servers a pep talk...",
  "MCP servers are tying their shoes...",
  "We swear it's almost ready...",
  "The tools are fashionably late...",
  "Teaching the servers some manners...",
  "Bribing the APIs with cookies...",
] as const;

function shuffleArray<T>(array: readonly T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export function ChatToolkitPickerAction() {
  const {
    toolkitCollection,
    isLoading,
    isError,
    selectedToolkits,
    selectedTools,
    onSelectionChange,
    defaultToolFqns,
  } = useChatToolkits();
  const [shuffledPhrases] = useState(() => shuffleArray(LOADING_PHRASES));
  const phraseIndexRef = useRef(0);
  const [loadingPhrase, setLoadingPhrase] = useState(() => shuffledPhrases[0]);

  useEffect(() => {
    if (!isLoading || shuffledPhrases.length < 2) {
      return;
    }

    const interval = setInterval(() => {
      phraseIndexRef.current =
        (phraseIndexRef.current + 1) % shuffledPhrases.length;
      setLoadingPhrase(shuffledPhrases[phraseIndexRef.current]);
    }, 2000);

    return () => clearInterval(interval);
  }, [isLoading, shuffledPhrases]);

  if (toolkitCollection && toolkitCollection.toolkits.length > 0) {
    return (
      <ToolkitPicker
        defaultToolFqns={defaultToolFqns}
        initialProps={{
          selectedToolkits,
          selectedTools,
          committedToolkits: selectedToolkits,
          committedTools: selectedTools,
        }}
        onSelectionChange={onSelectionChange}
        selectionMode="multi"
        toolkitCollection={toolkitCollection}
      >
        <ToolkitPickerTrigger
          countType="tools"
          maxVisibleIcons={3}
          size="lg"
          variant="outline"
        />
      </ToolkitPicker>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-9 items-center gap-2 rounded-md border border-border/60 bg-muted/30 px-3 shadow-xs">
        <Loader size="sm" text={loadingPhrase} variant="braille" />
      </div>
    );
  }

  return (
    <Button className="h-9 gap-2 px-3" disabled variant="outline">
      <Wrench className="size-4" />
      <span>{isError ? "Tools unavailable" : "No tools available"}</span>
    </Button>
  );
}
